import axios from "axios";

import {
  getAccessToken,
  getAccessTokenRevision,
  getAuthorizationToken,
  setAccessToken,
  setAuthenticationTiming,
  signalSessionEnded,
} from "../auth/tokenStore";
import { handleWebSocketTicketError } from "../auth/websocketLifecycle";

import { installPdfMultipartUpload } from "./pdfMultipartUpload";
import { installCaseLoadingRequests } from "./caseLoadingRequests.mjs";

const origin = process.env.REACT_APP_BACKEND_URL || "https://dev.simplefirm.com";
const browserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

const axiosOptions = {
  baseURL: origin,
  withCredentials: true,
  withXSRFToken: true,
  xsrfCookieName: "csrftoken",
  xsrfHeaderName: "X-CSRFToken",
  headers: {
    "Content-Type": "application/json",
    "X-Timezone": browserTimezone,
  },
};

const authTransport = axios.create(axiosOptions);
const api = axios.create(axiosOptions);
export const api_without_cancellation = axios.create(axiosOptions);

let csrfToken = null;
let refreshPromise = null;
const REFRESH_LOCK_NAME = "bp-auth-refresh";
const REFRESH_LEASE_KEY = "bp-auth-refresh-lease";
const REFRESH_LEASE_MS = 15_000;
const refreshOwner = `${Date.now()}-${Math.random().toString(36).slice(2)}`;

export const ensureCSRFToken = async () => {
  const response = await authTransport.get("/api/auth/csrf/");
  const issuedToken = response.data?.csrf_token;
  if (!issuedToken) {
    throw new Error("The authentication server did not issue a CSRF token.");
  }
  csrfToken = issuedToken;
  return issuedToken;
};

const performRefresh = async (signalFailure) => {
  try {
    const response = await authTransport.post("/api/auth/refresh/");
    setAuthenticationTiming({
      sessionExpiresIn: response.data.session_expires_in,
      idleExpiresIn: response.data.idle_expires_in,
    });
    setAccessToken(response.data.access_token);
    return response.data.access_token;
  } catch (error) {
    if (signalFailure) {
      const reason = error.response?.data?.code || "session_ended";
      signalSessionEnded(reason);
    }
    throw error;
  }
};

const waitForRefreshLease = () =>
  new Promise((resolve) => {
    let settled = false;
    const finish = () => {
      if (settled) return;
      settled = true;
      window.removeEventListener("storage", handleStorage);
      resolve();
    };
    const handleStorage = (event) => {
      if (event.key === REFRESH_LEASE_KEY) finish();
    };
    window.addEventListener("storage", handleStorage);
    window.setTimeout(finish, 250);
  });

const withStorageRefreshLease = async (startingRevision, signalFailure) => {
  for (;;) {
    const now = Date.now();
    let lease = null;
    try {
      lease = JSON.parse(localStorage.getItem(REFRESH_LEASE_KEY));
    } catch {
      lease = null;
    }

    if (!lease || lease.expiresAt <= now || lease.owner === refreshOwner) {
      const ownLease = {
        owner: refreshOwner,
        expiresAt: now + REFRESH_LEASE_MS,
      };
      localStorage.setItem(REFRESH_LEASE_KEY, JSON.stringify(ownLease));
      try {
        const confirmed = JSON.parse(localStorage.getItem(REFRESH_LEASE_KEY));
        if (confirmed?.owner === refreshOwner) break;
      } catch {
        return performRefresh(signalFailure);
      }
    }
    await waitForRefreshLease();
    const sharedToken = getAccessToken();
    if (getAccessTokenRevision() !== startingRevision && sharedToken) {
      return sharedToken;
    }
  }

  try {
    const sharedToken = getAccessToken();
    if (getAccessTokenRevision() !== startingRevision && sharedToken) {
      return sharedToken;
    }
    return await performRefresh(signalFailure);
  } finally {
    try {
      const lease = JSON.parse(localStorage.getItem(REFRESH_LEASE_KEY));
      if (lease?.owner === refreshOwner) {
        localStorage.removeItem(REFRESH_LEASE_KEY);
      }
    } catch {
      localStorage.removeItem(REFRESH_LEASE_KEY);
    }
  }
};

const coordinatedRefresh = async (startingRevision, signalFailure) => {
  if (navigator.locks?.request) {
    return navigator.locks.request(REFRESH_LOCK_NAME, async () => {
      const sharedToken = getAccessToken();
      if (getAccessTokenRevision() !== startingRevision && sharedToken) {
        return sharedToken;
      }
      return performRefresh(signalFailure);
    });
  }
  return withStorageRefreshLease(startingRevision, signalFailure);
};

export const refreshAccessToken = async ({ signalFailure = true } = {}) => {
  if (!refreshPromise) {
    const startingRevision = getAccessTokenRevision();
    refreshPromise = coordinatedRefresh(startingRevision, signalFailure).finally(() => {
      refreshPromise = null;
    });
  }
  return refreshPromise;
};

export const bootstrapAuthentication = async () => {
  await ensureCSRFToken();
  if (getAccessToken()) return true;
  try {
    await refreshAccessToken({ signalFailure: false });
    return true;
  } catch (error) {
    if (error.response?.status !== 401) throw error;
    return false;
  }
};

window.addEventListener("auth-token-sync-requested", () => {
  if (getAccessToken()) return;
  refreshAccessToken({ signalFailure: false }).catch(() => {});
});

const attachCSRFToken = (config) => {
  const method = (config.method || "get").toLowerCase();
  if (!csrfToken || ["get", "head", "options", "trace"].includes(method)) {
    return config;
  }

  const requestUrl = new URL(config.url || "", config.baseURL || origin);
  if (requestUrl.origin !== new URL(origin).origin) return config;

  config.headers = config.headers || {};
  config.headers["X-CSRFToken"] = csrfToken;
  return config;
};

const attachAuthorization = (config) => {
  if (config.skipAuth) return config;
  const requestUrl = new URL(config.url || "", config.baseURL || origin);
  const backendOrigin = new URL(origin).origin;
  if (requestUrl.origin !== backendOrigin) return config;
  const token = getAuthorizationToken();
  if (token) config.headers.Authorization = token;
  return config;
};

authTransport.interceptors.request.use(attachCSRFToken);
api.interceptors.request.use(attachCSRFToken);
api_without_cancellation.interceptors.request.use(attachCSRFToken);
api.interceptors.request.use(attachAuthorization);
api_without_cancellation.interceptors.request.use(attachAuthorization);

const shouldAttemptRefresh = (error) => {
  const status = error.response?.status;
  const config = error.config || {};
  const url = config.url || "";
  const requestUrl = new URL(url, config.baseURL || origin);
  return (
    status === 401 &&
    Boolean(getAuthorizationToken()) &&
    requestUrl.origin === new URL(origin).origin &&
    !config.skipAuth &&
    !config.__authRetried &&
    !url.includes("/api/login/") &&
    !url.includes("/api/auth/refresh/") &&
    !url.includes("/api/logout")
  );
};

const retryWithRefresh = async (error) => {
  if (!shouldAttemptRefresh(error)) return Promise.reject(error);
  const config = error.config;
  config.__authRetried = true;
  const currentAuthorization = getAuthorizationToken();
  if (
    currentAuthorization &&
    config.headers?.Authorization &&
    config.headers.Authorization !== currentAuthorization
  ) {
    config.headers.Authorization = currentAuthorization;
    return authTransport.request(config);
  }
  const accessToken = await refreshAccessToken();
  config.headers.Authorization = `Bearer ${accessToken}`;
  return authTransport.request(config);
};

api.interceptors.response.use((response) => response, retryWithRefresh);
api_without_cancellation.interceptors.response.use((response) => response, retryWithRefresh);
installPdfMultipartUpload(api);

// A number of legacy modules still use axios directly. Applying the same
// same-origin-only lifecycle here keeps refresh behavior centralized without
// leaking the BP credential to third-party integrations.
axios.defaults.withCredentials = true;
axios.defaults.withXSRFToken = true;
axios.defaults.xsrfCookieName = "csrftoken";
axios.defaults.xsrfHeaderName = "X-CSRFToken";
axios.interceptors.request.use(attachAuthorization);
axios.interceptors.response.use((response) => response, retryWithRefresh);

// Register after auth retries so an opening case waits for the final response.
[api, api_without_cancellation, axios].forEach((client) => {
  installCaseLoadingRequests(client, { backendOrigin: origin });
});

export const cancelPendingRequests = () => {};

export const requestWebSocketTicket = async (path) => {
  try {
    const response = await api.post("/api/auth/websocket-ticket/", { path });
    return response.data.ticket;
  } catch (error) {
    handleWebSocketTicketError(error, path);
    throw error;
  }
};

export const buildTicketedWebSocketURL = async (path, params = {}) => {
  const ticket = await requestWebSocketTicket(path);
  const base = process.env.REACT_APP_BACKEND_URL || "https://dev.simplefirm.com";
  const wsBase = base.replace(/^http/, "ws").replace(/\/$/, "");
  const query = new URLSearchParams({ ...params, ticket });
  return `${wsBase}${path}?${query.toString()}`;
};

export default api;
